import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import {
  formatPhoneNumber,
  formatDate,
  formatArrayField,
} from "../../../utils";
import UserColumnFilters from "./UserColumnFilters";
import styles from "./UserTable.module.css";

/**
 * UserTable - Component for displaying user data in a table format with filtering and pagination
 *
 * @param {Object} props - Component props
 * @param {Array} props.users - Array of user objects to display
 * @param {boolean} props.isLoading - Whether data is loading
 * @param {Function} props.onUserEdit - Callback when user edit is requested
 * @param {Object} props.columnFilters - Current active column filters
 * @param {Function} props.onColumnFilterChange - Callback when column filter changes
 * @param {string} props.activeFilterColumn - Currently active filter column
 * @param {Function} props.onToggleFilterColumn - Toggle filter column dropdown
 * @param {Function} props.onClearFilters - Clear all filters
 * @param {Function} props.onLoadMore - Load more data (pagination)
 * @param {boolean} props.hasMoreData - Whether more data is available to load
 */
const UserTable = ({
  users,
  isLoading,
  onUserEdit,
  columnFilters,
  onColumnFilterChange,
  activeFilterColumn,
  onToggleFilterColumn,
  onClearFilters,
  onLoadMore,
  hasMoreData,
}) => {
  // Function to get unique values for column filtering
  const getUniqueColumnValues = useCallback(
    (column) => {
      const values = new Set();

      // Always add "All" option
      values.add("all");

      // Add "Empty/N/A" option
      values.add("empty");

      // Add all unique values from the data
      users.forEach((user) => {
        const value = user[column];
        if (value && value !== "" && value !== "N/A") {
          if (typeof value === "string") {
            values.add(value);
          } else {
            values.add(String(value));
          }
        }
      });

      return Array.from(values);
    },
    [users]
  );

  // Render specific cell content based on column type
  const renderCellContent = useCallback((user, column) => {
    switch (column) {
      case "userId":
        return user.userId;
      case "name":
        return `${user.firstName || ""} ${user.lastName || ""}`;
      case "email":
        return user.email || "N/A";
      case "phone":
        return formatPhoneNumber(user.phone) || "N/A";
      case "sex":
        return user.zsex || "N/A";
      case "dateAdded":
        return formatDate(user.dateAdded) || "N/A";
      case "groupMeUserId":
        return user.groupMeUserId || "Missing";
      case "shirtsize":
        return user.shirtsize || "N/A";
      case "reInterestedInEvents":
      case "selectedEvents":
        return (
          <div className={styles.scrollableContent}>
            {(() => {
              try {
                // Special handling for DynamoDB formatted arrays
                if (
                  user[column] &&
                  typeof user[column] === "string" &&
                  user[column].includes('{"S":')
                ) {
                  try {
                    const parsed = JSON.parse(user[column]);
                    if (
                      Array.isArray(parsed) &&
                      parsed.length > 0 &&
                      parsed[0] &&
                      typeof parsed[0] === "object" &&
                      parsed[0].S !== undefined
                    ) {
                      return parsed
                        .map((item) => item.S || "")
                        .filter(Boolean)
                        .join(", ");
                    }
                  } catch (e) {
                    console.error("Error parsing special format:", e);
                  }
                }

                // Regular array handling
                if (Array.isArray(user[column])) {
                  return user[column].join(", ");
                }

                // String handling - assuming this is a string that should be comma-separated
                if (typeof user[column] === "string") {
                  // If it's already a comma-separated list, return as is
                  if (user[column].includes(",")) {
                    return user[column];
                  }

                  // Try to parse as JSON and join with commas
                  try {
                    const parsed = JSON.parse(user[column]);
                    if (Array.isArray(parsed)) {
                      return parsed.join(", ");
                    }
                  } catch (e) {
                    // Not valid JSON, continue with other methods
                  }

                  // Use the formatArrayField function
                  const formattedArray = formatArrayField(user[column]);
                  if (Array.isArray(formattedArray)) {
                    return formattedArray.join(", ");
                  }
                }

                // Fallback to the original string
                return user[column] || "N/A";
              } catch (error) {
                console.error(`Error formatting ${column}:`, error);
                return "Format Error";
              }
            })()}
          </div>
        );
      case "referredBy":
        return user.referredBy || "N/A";
      case "zmessage":
        return (
          <div className={styles.messageField}>{user.zmessage || "N/A"}</div>
        );
      case "socials":
        return user.socials || "N/A";
      default:
        return user[column] || "N/A";
    }
  }, []);

  const tableColumns = [
    { id: "actions", label: "Actions", width: "80px" },
    { id: "name", label: "Name", width: "140px" },
    { id: "shirtsize", label: "Shirt Size", width: "90px" },
    { id: "groupMeUserId", label: "GroupMe ID", width: "120px" },
    { id: "email", label: "Email", width: "160px" },
    { id: "phone", label: "Phone", width: "120px" },
    { id: "sex", label: "Sex", width: "80px" },
    { id: "dateAdded", label: "Added Date", width: "110px" },
    { id: "reInterestedInEvents", label: "Interested In", width: "160px" },
    { id: "selectedEvents", label: "Selected Events", width: "160px" },
    { id: "referredBy", label: "Referred By", width: "120px" },
    { id: "zmessage", label: "Message", width: "150px" },
    { id: "socials", label: "Socials", width: "120px" },
    { id: "userId", label: "UserId", width: "90px" },
  ];

  return (
    <div className={styles.tableContainer}>
      <div className={styles.tableHeader}>
        <h3>
          Users ({users.length})
          {hasMoreData && (
            <span className={styles.moreAvailable}>(More Available)</span>
          )}
          {Object.keys(columnFilters).length > 0 && (
            <>
              <span className={styles.activeFiltersIndicator}>
                ({Object.keys(columnFilters).length} active filter
                {Object.keys(columnFilters).length === 1 ? "" : "s"})
              </span>
              <button
                className={styles.clearFiltersBtn}
                onClick={onClearFilters}
              >
                Clear Filters
              </button>
            </>
          )}
        </h3>
      </div>

      {users.length === 0 && !isLoading ? (
        <div className={styles.noData}>
          No users found matching the current filters
        </div>
      ) : (
        <div className={styles.userTableContainer}>
          <div className={styles.tableScrollHint}>
            Scroll horizontally to view all fields ↔️
          </div>

          <table className={styles.userTable}>
            <thead>
              <tr>
                {tableColumns.map((column) => (
                  <th
                    key={column.id}
                    className={styles[`col-${column.id}`]}
                    style={{ width: column.width }}
                  >
                    {column.label}
                    {column.id !== "actions" && (
                      <UserColumnFilters
                        column={column.id}
                        filters={columnFilters}
                        onFilterChange={onColumnFilterChange}
                        onToggleFilterDropdown={onToggleFilterColumn}
                        activeFilter={activeFilterColumn}
                        uniqueValues={getUniqueColumnValues(column.id)}
                      />
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr
                  key={user.userId}
                  className={
                    user.zsex === "female"
                      ? styles.femaleRow
                      : user.zsex === "male"
                      ? styles.maleRow
                      : ""
                  }
                >
                  <td className={styles.actionCell}>
                    <button
                      className={styles.editBtn}
                      onClick={() => onUserEdit(user)}
                    >
                      Edit
                    </button>
                  </td>

                  {tableColumns.slice(1).map((column) => (
                    <td
                      key={column.id}
                      className={
                        column.id === "groupMeUserId" && !user.groupMeUserId
                          ? styles.missingField
                          : column.id === "zmessage"
                          ? styles.messageField
                          : ["reInterestedInEvents", "selectedEvents"].includes(
                              column.id
                            )
                          ? styles.multiLineCell
                          : ""
                      }
                      title={
                        typeof renderCellContent(user, column.id) === "string"
                          ? renderCellContent(user, column.id)
                          : ""
                      }
                    >
                      {renderCellContent(user, column.id)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>

          {hasMoreData && (
            <div className={styles.loadMore}>
              <button
                onClick={onLoadMore}
                disabled={isLoading}
                className={styles.loadMoreBtn}
              >
                {isLoading ? "Loading..." : "Load More Users"}
              </button>
              <div className={styles.paginationInfo}>
                Showing {users.length} users. More available.
              </div>
            </div>
          )}
        </div>
      )}

      {isLoading && (
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingSpinner}></div>
          <div className={styles.loadingText}>Loading users...</div>
        </div>
      )}
    </div>
  );
};

UserTable.propTypes = {
  users: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  onUserEdit: PropTypes.func.isRequired,
  columnFilters: PropTypes.object,
  onColumnFilterChange: PropTypes.func.isRequired,
  activeFilterColumn: PropTypes.string,
  onToggleFilterColumn: PropTypes.func.isRequired,
  onClearFilters: PropTypes.func.isRequired,
  onLoadMore: PropTypes.func,
  hasMoreData: PropTypes.bool,
};

UserTable.defaultProps = {
  users: [],
  isLoading: false,
  columnFilters: {},
  hasMoreData: false,
};

export default UserTable;
